import _ from 'lodash/fp'
import { all, call, put, take, takeLatest } from 'redux-saga/effects'
import gql from 'graphql-tag'
import { actions, types } from '../reducers/guidesData'
import * as guidesReducer from '../reducers/guides'
import { getEffect, postEffect, prependClubsUrl } from './network'
import { apolloClient } from '@fe/business/src/apollo'

export const DELETE_DISTANCE_MARKERS = gql`
  mutation DeleteDistanceMarkers($input: DeleteDistanceMarkersInput!) {
    deleteDistanceMarkers(input: $input)
  }
`

export const IMPORT_DISTANCE_MARKERS = gql`
  mutation ImportDistanceMarkers($input: ImportDistanceMarkersInput!) {
    importDistanceMarkers(input: $input)
  }
`

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loadGuidesDataEffect (guideId) {
  const request = {
    url: (yield call(prependClubsUrl, `guides/${guideId}/data`))
  }
  const chan = yield call(getEffect, request)
  return yield take(chan)
}

export function * saveDistancesEffect ({ guideId, holeId, distances }) {
  const body = _.map(_.flow(
    _.omit(['tees', 'green', 'distance', 'id']),
    _.update('destination', _.omit(['z']))
  ), distances)
  const request = {
    url: (yield call(prependClubsUrl, `guides/${guideId}/holes/${holeId}/distance_markers`)),
    options: {
      body
    }
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

export function* deleteDistancesEffect({ guideId }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_DISTANCE_MARKERS,
      variables: { input: { guideId } }
    })
    return { success: _.get('data.deleteDistanceMarkers', response) }
  } catch (error) {
    return { error }
  }
}

export function* importDistancesEffect({ targetGuideId, sourceGuideId, deleteExisting }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: IMPORT_DISTANCE_MARKERS,
      variables: {
        input: {
          targetGuideId,
          sourceGuideId,
          deleteExisting
        }
      }
    })
    return { success: _.get('data.importDistanceMarkers', response) }
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

/**
 * takeLatest(types.LOAD_REQUEST, loadGuidesDataFlow)
 */
export function * loadGuidesDataFlow ({ payload: guideId }) {
  const data = yield call(loadGuidesDataEffect, guideId)
  const error = _.get('error', data)
  if (error) {
    yield put(actions.loadError(error))
  } else {
    yield put(actions.loadSuccess(data))
  }
}

/**
 * takeLatest(types.SAVE_DISTANCES_REQUEST, saveDistancesRequestFlow)
 */
export function * saveDistancesRequestFlow ({ payload }) {
  yield put(actions.saveDistancesWaiting(true))
  const result = yield call(saveDistancesEffect, payload)
  const error = _.get('error', result)
  if (error) {
    yield put(actions.saveDistancesError(error))
  } else {
    yield put(guidesReducer.actions.setDistances(payload))
    yield put(actions.saveDistancesSuccess(result))
  }
  yield put(actions.saveDistancesWaiting(false))
}

/**
 * takeLatest(types.DELETE_DISTANCES_REQUEST, deleteDistancesRequestFlow)
 */
export function* deleteDistancesRequestFlow({ payload: guideId }) {
  yield put(actions.deleteDistancesWaiting(true))

  const result = yield call(deleteDistancesEffect, { guideId })
  const error = _.get('error', result)

  if (error) {
    yield put(actions.deleteDistancesError(error))
  } else if (result.success) {
    yield put(actions.deleteDistancesSuccess(guideId))
  } else {
    yield put(actions.deleteDistancesError('Deletion failed'))
  }

  yield put(actions.deleteDistancesWaiting(false))
}
/**
 * takeLatest(types.IMPORT_DISTANCES_REQUEST, importDistancesRequestFlow)
 */
export function* importDistancesRequestFlow({ payload: { targetGuideId, sourceGuideId, deleteExisting } }) {
  yield put(actions.importDistancesWaiting(true))

  const result = yield call(importDistancesEffect, { targetGuideId, sourceGuideId, deleteExisting })
  const error = _.get('error', result)

  if (error) {
    yield put(actions.importDistancesError(error))
  } else if (result.success) {
    yield put(actions.loadRequest(targetGuideId))
    yield put(actions.importDistancesSuccess({ guideId: targetGuideId }))
  } else {
    yield put(actions.importDistancesError('Import failed'))
  }

  yield put(actions.importDistancesWaiting(false))
}

export default function * () {
  return yield all([
    takeLatest(types.LOAD_REQUEST, loadGuidesDataFlow),
    takeLatest(types.SAVE_DISTANCES_REQUEST, saveDistancesRequestFlow),
    takeLatest(types.DELETE_DISTANCES_REQUEST, deleteDistancesRequestFlow),
    takeLatest(types.IMPORT_DISTANCES_REQUEST, importDistancesRequestFlow)
  ])
}
